import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { SchemaRegion } from "../../../../api/restaurantSchema";
import { SlidersIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { Field, TextField } from "./formControls";
import { GhostButton, PrimaryButton, SchemaModalShell } from "./SchemaModalShell";

const SMALL_BUTTON =
  "flex h-9 shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 text-[13px] font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * „Редакция на региона": името (записва се с „Запази") и отделна зона за
 * изтриване — само на празен регион, през потвърждение в родителя.
 */
const RegionSettingsModal = ({
  open,
  region,
  onSave,
  onDelete,
  onClose,
}: {
  open: boolean;
  region: SchemaRegion;
  onSave: (name: string) => Promise<void>;
  onDelete: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(region.name);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tableCount = region.elements.filter((element) => element.kind === "table").length;
  const objectCount = region.elements.length - tableCount;
  const isEmpty = region.elements.length === 0;
  const canSave = name.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSave(name.trim());
    } catch {
      // Грешката е показана от родителя.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SchemaModalShell
      open={open}
      size="form"
      icon={<SlidersIcon className="h-[19px] w-[19px]" />}
      title={t("restaurantSchema.regionSettings.title")}
      subtitle={t("restaurantSchema.regionSettings.subtitle")}
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={isSubmitting}>
            {t("common.cancel")}
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={!canSave} loading={isSubmitting}>
            {t("common.save")}
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <Field label={t("restaurantSchema.region.name")} required>
          <TextField value={name} onChange={setName} onEnter={handleSave} maxLength={60} />
        </Field>

        <div className="flex items-center gap-3.5 rounded-[12px] border border-danger-ink/20 bg-danger-tint/50 px-4 py-3.5">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="text-[13.5px] font-semibold text-danger-ink">
              {t("restaurantSchema.regionSettings.deleteTitle")}
            </span>
            <span className="text-[12.5px] leading-[1.4] text-danger-ink/85">
              {isEmpty
                ? t("restaurantSchema.regionSettings.deleteEmpty")
                : t("restaurantSchema.regionSettings.deleteBlocked", {
                    tables: t("restaurantSchema.counts.tables", { count: tableCount }),
                    objects: t("restaurantSchema.counts.objects", { count: objectCount }),
                  })}
            </span>
          </div>
          {/* Само празен регион се трие (BE също връща 409) — иначе бутонът е
              неактивен, а текстът казва какво да се премахне първо. */}
          <button
            type="button"
            onClick={onDelete}
            disabled={isSubmitting || !isEmpty}
            className={`${SMALL_BUTTON} border border-danger-ink/35 bg-surface px-3.5 text-danger-ink hover:bg-danger-tint disabled:hover:bg-surface`}
          >
            {t("restaurantSchema.regionSettings.delete")}
          </button>
        </div>
      </div>
    </SchemaModalShell>
  );
};

export default RegionSettingsModal;
